import type { Locale } from '@mfw/translations';

import { getLocalizedNameByLocale } from '@mfw/components';
import groupBy from 'lodash/groupBy';
import omit from 'lodash/omit';
import { v4 as uuidv4 } from 'uuid';

import type {
  UiProgramCreate,
  ApiProgram,
  UiProgram,
} from '../../schemas/programs.schema';
import type { ApiContentConfigurationPayload } from '../../schemas/contentConfiguration.schema';

import {
  mapContentConfigurationFromApi,
  mapContentConfigurationToApi,
} from './contentConfiguration.mappers';
import { mapCategoryWithTagsFromApi } from './categories.mappers';
import { mapTranslationsFromApi } from './translations.mappers';
import { DEFAULT_CURRENCY } from '../../config/constants';

type ApiProgramWorkoutPayload = ApiProgram['weeks'][number]['workout'];

export function createProgramApiArgs(
  program: Omit<UiProgramCreate, 'coverSource' | 'coverType'> & {
    coverSource: null | string;
    coverType: null | string;
  },
) {
  let tags: string[] = [];

  if (program.categories && Object.keys(program.categories).length) {
    tags = Object.values(program.categories).flatMap((category) => {
      return category?.flatMap((tag) => tag.value) ?? [];
    });
  }

  const contentConfiguration: ApiContentConfigurationPayload =
    mapContentConfigurationToApi(program);

  const args = omit(
    {
      ...program,
      ...contentConfiguration,
      tags,
      translations: program.translations.map((translation) => ({
        description: translation.description,
        locale: translation.locale?.[0]?.value,
        name: translation.name,
      })),
      weeks: program.weeks.flatMap((week, index) => {
        return week.workouts.map((workout) => ({
          day: workout.day?.[0]?.value.toUpperCase(),
          weekIndex: index,
          weekName: week.weekName,
          workoutId: workout.workout.id,
          alternateWorkoutId: workout.alternates?.[0]?.id,
        }));
      }),
    },
    [
      'categories',
      'coverFile',
      'mediaFile',
      'coverSignedUrl',
      'mediaSignedUrl',
      'locale',
      'isMarketplace',
      'isOrg',
      'marketplaceCurrency',
      // form metadata for the 9.6 price floor — never part of the payload
      'marketplaceListedPrice',
      'marketplacePrice',
      'organizationIds',
    ],
  );
  return args;
}

function mapProgramWorkoutFromApi(
  workout: ApiProgramWorkoutPayload,
  locale: Locale,
) {
  return {
    id: workout.id,
    author: {
      name: workout?.author?.name || '',
    },
    categories: mapCategoryWithTagsFromApi({
      categories: workout.categories,
      locale,
    }),
    coverSignedUrl: workout.coverSignedUrl,
    coverSource: workout.coverSource,
    coverType: workout.coverType,
    exerciseCount: workout.exerciseCount,
    isPlatform: workout.isPlatform ?? false,
    isPremium: workout.isPremium ?? false,
    level: workout.level,
    organizationId: workout.organizationId,
    translations: mapTranslationsFromApi(workout.translations),
    uniqueId: uuidv4(),
  };
}

export function mapProgramFromApi(
  program: ApiProgram,
  locale: Locale,
): UiProgram {
  const categories = mapCategoryWithTagsFromApi({
    categories: program.categories,
    locale,
  });
  const groupedWeeks = groupBy(program.weeks, 'weekIndex');

  const allWeeks: UiProgram['weeks'][] = [];

  // A REST WEEK has no row, only a gap in `weekIndex`: it is put back, empty,
  // because the editor's array position IS the week (`createProgramApiArgs`
  // sends `weekIndex: index`) — dropped, it was lost on every save, and every
  // calendar row after it moved a week earlier. Empty weeks before the first
  // workout stay out: the editor refuses an empty first week, and a program
  // is placed from its first workout.
  const weekIndexes = Object.keys(groupedWeeks).map(Number);
  const firstWeek = Math.min(...weekIndexes);
  const lastWeek = Math.max(...weekIndexes);
  for (let weekIndex = firstWeek; weekIndex <= lastWeek; weekIndex++) {
    const weeks = groupedWeeks[weekIndex] ?? [];
    const weekWithWorkouts = {
      id: uuidv4(),
      weekName: weeks[0]?.weekName ?? '',
      workouts: [],
    } as any;
    weeks.forEach((week) => {
      weekWithWorkouts.workouts.push({
        id: uuidv4(),
        day: [{ value: week.day.toLowerCase() }],
        workout: mapProgramWorkoutFromApi(week.workout, locale),
        alternates: week.alternates?.map((alternate) =>
          mapProgramWorkoutFromApi(alternate, locale),
        ),
      });
    });
    allWeeks.push(weekWithWorkouts);
  }

  const contentConfiguration = mapContentConfigurationFromApi(program);
  return {
    id: program.id,
    // TOOD API to add author
    author: {
      id: program?.author?.id,
      name: program?.author?.name || '',
    },
    categories,
    configuration: contentConfiguration,
    coverSignedUrl: program?.coverSignedUrl,
    coverSource: program?.coverSource,
    coverType: program?.coverType,
    createdAt: program.createdAt,
    isDeleted: program.isDeleted,
    isMarketplace: program?.configuration?.marketplace?.enabled,
    level: program.level,
    marketplaceCurrency:
      program?.configuration?.marketplace?.currency ?? DEFAULT_CURRENCY,
    marketplaceId: program?.configuration?.marketplace?.id ?? null,
    marketplaceInLibrary:
      program?.configuration?.marketplace?.inLibrary ?? false,
    marketplaceRestorable:
      program?.configuration?.marketplace?.restorable ?? false,
    marketplaceLibraryId:
      program?.configuration?.marketplace?.libraryId ?? null,
    marketplacePrice: program?.configuration?.marketplace?.enabled
      ? (program?.configuration?.marketplace?.price ?? 0)
      : null,
    numberOfExercises: program.numberOfExercises,
    numberOfWeeks: program.numberOfWeeks,
    numberOfWorkouts: program.numberOfWorkouts,
    organizationId: program?.organizationId,
    previewOnly: program.previewOnly,
    organizationIds:
      program?.configuration?.organizationIds?.map((org) => ({
        label: '',
        value: org,
      })) ?? [],
    translations: program.translations?.map((translation) => ({
      id: translation.id,
      description: translation.description,
      locale: [
        {
          label: getLocalizedNameByLocale(translation.locale),
          value: translation.locale as Locale,
        },
      ],
      name: translation.name,
    })),
    uniqueId: uuidv4(),
    updatedAt: program.updatedAt,
    weeks: allWeeks as any,
  };
}
